import {
  DOT,
  EMPTY,
  MAX_CLUE,
  MAX_SIZE,
  SHADED,
  type Clue,
  type NurikabeDefinition,
  type NurikabeProgress,
  type NurikabeSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Shade some cells so that every unshaded area (an island) contains exactly one number, equal to how many cells it has. All shaded cells form a single wall, connected horizontally and vertically, and never cover a 2×2 square. Numbered cells stay unshaded.",
  fr: "Noircissez des cases de sorte que chaque zone restée blanche (une île) contienne exactement un nombre, égal à son nombre de cases. Les cases noircies forment un seul mur, relié horizontalement et verticalement, qui ne couvre jamais un carré 2×2. Les cases numérotées restent blanches.",
};

export function blankClues(rows: number, cols: number): Clue[][] {
  return Array.from({ length: rows }, () => Array<Clue>(cols).fill(null));
}

export function emptyDefinition(): NurikabeDefinition {
  return {
    puzzleType: "nurikabe",
    typeVersion: 1,
    rows: 7,
    cols: 7,
    clues: blankClues(7, 7),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function blankRows(rows: number, cols: number): string[] {
  return Array.from({ length: rows }, () => EMPTY.repeat(cols));
}

/** Crops or pads a row set to the given size, keeping the top-left corner. */
export function resizeRows(source: string[], rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, r) => {
    const row = source[r] ?? "";
    return row.length >= cols ? row.slice(0, cols) : row + EMPTY.repeat(cols - row.length);
  });
}

/** Crops or pads the numbers to the given size, keeping the top-left corner. */
export function resizeClues(source: Clue[][], rows: number, cols: number): Clue[][] {
  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      const n = source[r]?.[c];
      return typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= MAX_CLUE ? n : null;
    }),
  );
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

/** Marks never sit on a number: whatever a stored blob says there is dropped. */
export function clearClues(marks: string[], clues: Clue[][]): string[] {
  return marks.map((row, r) =>
    Array.from(row, (char, c) => (typeof clues[r]?.[c] === "number" ? EMPTY : char)).join(""),
  );
}

export function emptyProgress(definition: NurikabeDefinition): NurikabeProgress {
  return { version: 1, rows: blankRows(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: NurikabeDefinition, raw: unknown): NurikabeProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [SHADED, DOT, EMPTY]));
  return {
    version: 1,
    rows: clearClues(resizeRows(rows, definition.rows, definition.cols), definition.clues),
  };
}

/** The server only reads the wall: dots are the player's own notes. */
export function serializeSubmission(
  definition: NurikabeDefinition,
  progress: NurikabeProgress,
): NurikabeSubmission {
  return {
    rows: resizeRows(progress.rows, definition.rows, definition.cols).map((row) =>
      row.replaceAll(DOT, EMPTY),
    ),
  };
}

/** The numbers as text, one line per row, `.` for a cell without one (the import format). */
export function formatClues(clues: Clue[][]): string {
  return clues.map((row) => row.map((n) => (n === null ? "." : String(n))).join(" ")).join("\n");
}

/**
 * Reads the text format back: cells separated by spaces or commas, or one character each on
 * a line without separators. Anything that is not a number from 1 up is a cell without one.
 */
export function parseClues(text: string, rows: number, cols: number): Clue[][] {
  const lines = text.split("\n");
  return Array.from({ length: rows }, (_, r) => {
    const line = (lines[r] ?? "").trim();
    const tokens = /[\s,;]/.test(line) ? line.split(/[\s,;]+/) : Array.from(line);
    return Array.from({ length: cols }, (_, c) => {
      const token = tokens[c];
      const n = token !== undefined && /^\d+$/.test(token) ? Number(token) : 0;
      return n >= 1 && n <= MAX_CLUE ? n : null;
    });
  });
}
